// ADO-3693: the product page's high-resolution image (BrandBank's HD copy of the small image,
// `ProductDetail.hdImageUrl`).
//
// AC1: the hero shows the small image at once and the HD copy once it has loaded. The HD image is
//      requested beside the small one, invisible and hidden from assistive tech, and replaces it
//      once it has loaded and decoded; the box keeps its 1:1 shape throughout, so nothing moves.
// AC2: the zoom dialog shows the HD copy (`useProductHdImage().src`).
// AC3: an HD file that fails to load is never asked for again, in the hero or the zoom, and the
//      small image stays. A product with no small image keeps the placeholder and asks for no
//      HD image at all, so HD is only ever an upgrade of an image the page already shows.
// AC4: only the product page reads the HD address. It comes from the page's detail slice
//      (`detailBySku`), which is filled on both routes onto the page: a direct visit's `load`,
//      and the `loadDetail` that runs beside a listing card's handed summary (no HD on it).
import { useState } from 'react';
import { useProductDetailStore } from '../../product-detail/useProductDetailStore';
import { ProductImage } from '../Browse/ProductImage';
import { safeImageHref } from '../Browse/safeImageHref';
import styles from './ProductHeroImage.module.css';

export interface ProductHdImage {
  /** The HD address to show, or `null`: none on file, not http(s), no small image, or failed. */
  readonly src: string | null;
  /** True once the HD image has loaded in the hero. */
  readonly loaded: boolean;
  readonly markLoaded: (src: string) => void;
  readonly markFailed: (src: string) => void;
}

/**
 * The product's HD image state, shared by the hero and its zoom dialog so a failure seen in one
 * is not requested again by the other. `smallHref` is the hero's own (scheme-checked) small image
 * address; without one there is no HD image.
 */
export function useProductHdImage(sku: string, smallHref: string | null): ProductHdImage {
  const hdImageUrl = useProductDetailStore((state) => state.detailBySku[sku]?.detail?.hdImageUrl);
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  // The address is scheme-checked like the small one: it is shown full size in the zoom dialog.
  const candidate = smallHref === null ? null : safeImageHref(hdImageUrl);
  const src = candidate !== null && candidate !== failedSrc ? candidate : null;
  return {
    src,
    loaded: src !== null && src === loadedSrc,
    markLoaded: setLoadedSrc,
    markFailed: setFailedSrc,
  };
}

export interface ProductHeroImageProps {
  /** The small image, as every other surface shows it; `null` renders the placeholder. */
  readonly src: string | null;
  readonly alt: string;
  readonly hd: ProductHdImage;
}

export function ProductHeroImage({ src, alt, hd }: ProductHeroImageProps) {
  const hdSrc = hd.src;
  return (
    <div className={styles.frame}>
      {/* The small image stays until the HD copy has loaded, then gives way to it. */}
      {hd.loaded ? null : <ProductImage src={src} alt={alt} priority />}
      {hdSrc === null ? null : (
        <img
          // A new address is a new element, so a previous product's load never shows it.
          key={hdSrc}
          className={hd.loaded ? styles.hd : `${styles.hd} ${styles.hdLoading}`}
          src={hdSrc}
          alt={hd.loaded ? alt : ''}
          aria-hidden={hd.loaded ? undefined : true}
          decoding="async"
          data-hd-image={hd.loaded ? 'shown' : 'loading'}
          // `load` fires before a large image is decoded, so the swap waits for `decode()`:
          // unmounting the small image earlier would leave the box blank for a frame. A
          // rejected decode still swaps (the image did load). A decode that settles after
          // this element left the page, or after its address changed, marks nothing.
          onLoad={(event) => {
            const image = event.currentTarget;
            const swap = () => {
              if (image.isConnected && image.getAttribute('src') === hdSrc) hd.markLoaded(hdSrc);
            };
            image.decode().then(swap, swap);
          }}
          onError={() => hd.markFailed(hdSrc)}
        />
      )}
    </div>
  );
}
